import { captureException } from '@sentry/react'
import { getPaymentStatus } from 'api/paymentApi'
import { withTimeout } from 'utils/withTimeout'

// Cap each probe + keep attempts short so authorise-timeout + poll stays under Apple's ~30s ceiling.
const STATUS_TIMEOUT_MS = 3000

// After a failed authorise we don't know if the charge actually went through. Poll the payment
// status and return its confirmation/failure URL if it resolved (else null), so the caller can
// recover a succeeded payment instead of showing a false failure.
export const resolvePaymentReturnUrl = async (
    paymentReference: string,
    attempts = 3,
    intervalMs = 1500
): Promise<string | null> => {
    for (let attempt = 0; attempt < attempts; attempt++) {
        try {
            // Check before sleeping: an already-committed payment is recovered instantly.
            const status = await withTimeout(getPaymentStatus(paymentReference, null), STATUS_TIMEOUT_MS)
            if (status.isPaymentResultAvailable && status.returnUrl) {
                return status.returnUrl
            }
        } catch (e: unknown) {
            // Couldn't reach status in time — log (don't fail silently) and keep polling.
            captureException(e, {
                level: 'warning',
                tags: { component: 'resolvePaymentReturnUrl', context: 'getPaymentStatus' },
                extra: { paymentReference, attempt },
            })
        }

        if (attempt < attempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, intervalMs))
        }
    }

    return null
}
